import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Card, Col, Row, Segmented, Space, Spin, Typography, message,
} from 'antd';
import { DollarOutlined, ReloadOutlined } from '@ant-design/icons';
import MetricCard from '../../components/reports/MetricCard';
import TrendSparkline from '../../components/reports/TrendSparkline';
import {
  getCashSnapshot,
  getPnlStatement,
  getExpenses,
  deleteExpense,
  formatCents,
  CashSnapshot,
  PnlStatement,
  Expense,
  PnlRange,
} from '../../services/moneyApi';
import { ExpenseEntry, ExpenseList } from '../../components/Money';

const { Title, Text } = Typography;

type MoneyRange = PnlRange;

const RANGE_OPTIONS: Array<{ label: string; value: MoneyRange }> = [
  { label: '7D',  value: '7d'   },
  { label: '30D', value: '30d'  },
  { label: '90D', value: '90d'  },
  { label: 'YTD', value: '365d' },
];

/**
 * Owner-only Money / P&L dashboard. Route-gated by /owner/* PrivateRoute.
 * All amounts arrive as integer cents; formatCents() handles display only.
 */
const MoneyDashboard: React.FC = () => {
  const [range, setRange] = useState<MoneyRange>('30d');
  const [snapshot, setSnapshot] = useState<CashSnapshot | null>(null);
  const [pnl, setPnl] = useState<PnlStatement | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async (r: MoneyRange) => {
    setLoading(true);
    try {
      const [snap, statement, exps] = await Promise.all([
        getCashSnapshot(5),
        getPnlStatement(r),
        getExpenses(),
      ]);
      setSnapshot(snap);
      setPnl(statement);
      setExpenses(exps);
    } catch (err) {
      console.error('MoneyDashboard fetch error:', err);
      message.error('Failed to load money data — please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRangeChange = (value: string | number) => {
    const r = value as MoneyRange;
    setRange(r);
    fetchAll(r);
  };

  const handleExpenseAdded = () => {
    fetchAll(range);
  };

  const handleExpenseDelete = async (id: string) => {
    try {
      await deleteExpense(id);
      setExpenses((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      console.error('delete expense error:', err);
      message.error('Failed to delete expense — please try again.');
    }
  };

  // Sparkline points: net per day (divide cents → dollars for the SVG scale only)
  const sparkPoints = pnl
    ? pnl.daily.map((d) => ({ label: d.date, value: d.netCents / 100 }))
    : [];

  return (
    <div
      data-testid="money-dashboard"
      style={{ padding: '16px 12px', maxWidth: '100%', boxSizing: 'border-box' }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>

        {/* Header */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <DollarOutlined style={{ fontSize: 22, color: '#16a34a' }} />
            <Title level={2} style={{ margin: 0 }}>Money / P&amp;L</Title>
          </div>
          <Text type="secondary">
            Owner-only — cash position, profit, and expenses.
          </Text>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <Segmented
            data-testid="money-range"
            options={RANGE_OPTIONS}
            value={range}
            onChange={handleRangeChange}
            size="middle"
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchAll(range)}
            loading={loading}
            data-testid="money-refresh"
          >
            Refresh
          </Button>
        </div>

        <Spin spinning={loading} tip="Loading money data…">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>

            {/* Row 1 — Snapshot tiles */}
            <Row gutter={[12, 12]}>
              <Col xs={12} sm={6}>
                <MetricCard
                  title="Estimates (Last 5 Days)"
                  value={snapshot ? formatCents(snapshot.estimateValueCents) : null}
                  subtitle="New estimate value"
                  valueColor="#2563eb"
                  testId="tile-estimate-5d"
                />
              </Col>
              <Col xs={12} sm={6}>
                <MetricCard
                  title="Completed Work"
                  value={snapshot ? formatCents(snapshot.completedWorkValueCents) : null}
                  subtitle="Outstanding on completed WOs"
                  valueColor="#7c3aed"
                  testId="tile-completed"
                />
              </Col>
              <Col xs={12} sm={6}>
                <MetricCard
                  title="Waiting for Payment (A/R)"
                  value={snapshot ? formatCents(snapshot.accountsReceivableCents) : null}
                  subtitle="Total balance due"
                  valueColor="#dc2626"
                  testId="tile-ar"
                />
              </Col>
              <Col xs={12} sm={6}>
                <MetricCard
                  title="Collected"
                  value={snapshot ? formatCents(snapshot.collectedCents) : null}
                  subtitle="Total payments received"
                  valueColor="#16a34a"
                  testId="tile-collected"
                />
              </Col>
            </Row>

            {/* Row 2 — P&L statement + sparkline */}
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Card
                  title="Rolling P&amp;L Statement"
                  size="small"
                  data-testid="pnl-statement"
                >
                  {pnl ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '4px 0' }}><Text>Income (pre-tax)</Text></td>
                          <td style={{ textAlign: 'right' }}>
                            <Text strong style={{ color: '#16a34a' }}>{formatCents(pnl.incomeCents)}</Text>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '4px 0' }}>
                            <Text type="secondary">Sales tax collected (pass-through)</Text>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <Text type="secondary">{formatCents(pnl.taxCollectedCents)}</Text>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '4px 0' }}><Text>COGS (parts + labor)</Text></td>
                          <td style={{ textAlign: 'right' }}>
                            <Text style={{ color: '#dc2626' }}>− {formatCents(pnl.cogsCents)}</Text>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '4px 0' }}><Text>Expenses</Text></td>
                          <td style={{ textAlign: 'right' }}>
                            <Text style={{ color: '#dc2626' }}>− {formatCents(pnl.expenseCents)}</Text>
                          </td>
                        </tr>
                        <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px 0 4px' }}>
                            <Text strong style={{ fontSize: 15 }}>Net Profit</Text>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <Text
                              strong
                              data-testid="pnl-net"
                              style={{
                                fontSize: 18,
                                color: pnl.netCents >= 0 ? '#16a34a' : '#dc2626',
                              }}
                            >
                              {formatCents(pnl.netCents)}
                            </Text>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <Text type="secondary">No data.</Text>
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <TrendSparkline
                  title="Daily Net Profit"
                  points={sparkPoints}
                  color={pnl && pnl.netCents >= 0 ? '#16a34a' : '#dc2626'}
                  testId="sparkline-daily-net"
                />
              </Col>
            </Row>

            {/* Row 3 — Expenses */}
            <Card title="Expenses" size="small">
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <ExpenseEntry onAdded={handleExpenseAdded} />
                <ExpenseList
                  expenses={expenses}
                  loading={loading}
                  onDelete={handleExpenseDelete}
                />
              </Space>
            </Card>

          </Space>
        </Spin>
      </Space>
    </div>
  );
};

export default MoneyDashboard;
